import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, API, getStoredToken, extractErrorCode } from "@/lib/api";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  FileText,
  Upload,
  Loader2,
  Send,
  Trash2,
  Sparkles,
  Scale,
} from "lucide-react";
import { toast } from "sonner";

export default function Documents() {
  const { t } = useI18n();
  const { refresh, user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [activeDoc, setActiveDoc] = useState(null);
  const [messages, setMessages] = useState([]);
  const [question, setQuestion] = useState("");
  const [uploading, setUploading] = useState(false);
  const [asking, setAsking] = useState(false);
  // Legal mode: default ON for Legal Pro tier, OFF otherwise. Users can toggle
  // it explicitly on the chat area below the document title.
  const [legalMode, setLegalMode] = useState(false);
  const [legalDefaultApplied, setLegalDefaultApplied] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  const loadDocs = async () => {
    const r = await api.get("/documents");
    setDocs(r.data.documents || []);
  };

  useImperativeLoad(() => { loadDocs(); }, []);

  // Default Legal Mode ON for Legal Pro subscribers (one-shot on user resolve).
  useImperativeLoad(() => {
    if (legalDefaultApplied) return;
    if (user?.subscription_tier === "legal_pro") setLegalMode(true);
    if (user) setLegalDefaultApplied(true);
  }, [user, legalDefaultApplied]);

  useImperativeLoad(() => {
    if (activeDoc?.document_id) {
      api.get(`/documents/${activeDoc.document_id}/messages`).then((r) => {
        setMessages(r.data.messages || []);
      });
    } else {
      setMessages([]);
    }
  }, [activeDoc?.document_id]);

  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = getStoredToken();
      const resp = await fetch(`${API}/documents/upload`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
        credentials: "include",
      });
      const data = await resp.json();
      if (!resp.ok) {
        const code = data?.detail?.error_code || "UNKNOWN_ERROR";
        const translated = t(`documents.err_${code}`);
        toast.error(typeof translated === "string" && !translated.startsWith("documents.") ? translated : `Error: ${code}`);
        return;
      }
      toast.success(t("documents.upload_success"));
      await loadDocs();
      setActiveDoc(data);
      refresh();

      // Smart Legal-Mode suggestion: backend has already done a cheap regex
      // scan on the first 5k chars. If it looks like a legal doc AND the user
      // isn't already in legal mode, surface a non-intrusive toast with a
      // 1-click "Enable" action.
      if (data?.is_legal_document && !legalMode) {
        toast(t("documents.legal_detected_title"), {
          description: t("documents.legal_detected_desc"),
          duration: 12000,
          action: {
            label: t("documents.legal_detected_cta"),
            onClick: () => setLegalMode(true),
          },
        });
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleAsk = async () => {
    if (!question.trim() || !activeDoc || asking) return;
    const q = question.trim();
    setQuestion("");
    setAsking(true);

    const optimistic = {
      message_id: `tmp_${Date.now()}`,
      role: "user",
      content: q,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);

    try {
      const r = await api.post(`/documents/${activeDoc.document_id}/chat`, {
        question: q,
        legal_mode: legalMode,
      });
      setMessages((m) => [...m, r.data.assistant_message]);
      refresh();
    } catch (err) {
      const code = extractErrorCode(err);
      const translated = t(`documents.err_${code}`);
      toast.error(typeof translated === "string" && !translated.startsWith("documents.") ? translated : `Error: ${code}`);
      setMessages((m) => m.filter((x) => x.message_id !== optimistic.message_id));
    } finally {
      setAsking(false);
    }
  };

  const handleDelete = async (id, e) => {
    e?.stopPropagation();
    await api.delete(`/documents/${id}`);
    if (activeDoc?.document_id === id) setActiveDoc(null);
    loadDocs();
  };

  return (
    <div className="flex h-full" data-testid="documents-page">
      <aside className="flex w-72 flex-col border-r border-slate-800 bg-[#070924]">
        <div className="border-b border-slate-800 p-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="document-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full justify-start rounded-lg bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
            data-testid="document-upload-btn"
          >
            {uploading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("documents.uploading")}</>
            ) : (
              <><Upload className="mr-2 h-4 w-4" />{t("documents.upload_btn")}</>
            )}
          </Button>
          <p className="mt-2 text-[11px] text-slate-500">{t("documents.upload_hint")}</p>
        </div>

        <ScrollArea className="flex-1">
          {docs.length === 0 ? (
            <p className="p-4 text-xs text-slate-500" data-testid="documents-empty">{t("documents.no_documents")}</p>
          ) : (
            <ul className="space-y-1 p-2">
              {docs.map((d) => (
                <li
                  key={d.document_id}
                  className={`group flex cursor-pointer items-start gap-2 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                    activeDoc?.document_id === d.document_id
                      ? "bg-[#9ACD32]/15 text-[#9ACD32]"
                      : "text-slate-300 hover:bg-slate-800/40"
                  }`}
                  onClick={() => setActiveDoc(d)}
                  data-testid={`document-item-${d.document_id}`}
                >
                  <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-60" />
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-1 text-[13px] font-medium">{d.filename}</p>
                    <p className="mt-0.5 text-[10px] text-slate-500">
                      {d.page_count} {t("documents.pages")} · {d.char_count.toLocaleString()} {t("documents.chars")}
                    </p>
                  </div>
                  <button
                    className="opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-400"
                    onClick={(e) => handleDelete(d.document_id, e)}
                    data-testid={`document-delete-${d.document_id}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ScrollArea>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="border-b border-slate-800 px-8 py-5">
          <h1 className="font-display text-2xl font-bold text-white">{t("documents.title")}</h1>
          <p className="mt-1 text-sm text-slate-400">{t("documents.subtitle")}</p>
        </header>

        {activeDoc ? (
          <>
            <div className="border-b border-slate-800 bg-[#070924] px-8 py-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#9ACD32]/15">
                  <FileText className="h-5 w-5 text-[#9ACD32]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-white" data-testid="document-active-title">{activeDoc.filename}</h2>
                  <p className="text-xs text-slate-500">
                    {activeDoc.page_count} {t("documents.pages")} · {activeDoc.char_count.toLocaleString()} {t("documents.chars")}
                  </p>
                </div>
                {/* Legal mode toggle — apply specialized IT/EU contract analyst on next question */}
                <button
                  type="button"
                  onClick={() => setLegalMode((v) => !v)}
                  aria-pressed={legalMode}
                  className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                    legalMode
                      ? "border-[#D4A937] bg-[#D4A937]/15 text-[#F4C955]"
                      : "border-slate-700 text-slate-400 hover:border-[#D4A937]/40 hover:text-[#F4C955]"
                  }`}
                  data-testid="document-legal-mode-toggle"
                  title={legalMode ? t("documents.legal_mode_on_hint") : t("documents.legal_mode_off_hint")}
                >
                  <Scale className="h-3 w-3" />
                  {legalMode ? t("documents.legal_mode_on") : t("documents.legal_mode_off")}
                </button>
              </div>
              {legalMode && (
                <div
                  className="mt-3 flex items-start gap-2 rounded-lg border border-[#D4A937]/30 bg-[#D4A937]/10 p-3 text-xs text-[#F4C955]"
                  data-testid="document-legal-banner"
                >
                  <Scale className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span className="leading-relaxed">{t("documents.legal_mode_banner")}</span>
                </div>
              )}
              {activeDoc.truncated && (
                <div
                  className="mt-3 flex items-start gap-2 rounded-lg border border-orange-500/30 bg-orange-500/10 p-3 text-xs text-orange-200"
                  data-testid="document-truncated-banner"
                >
                  <span className="leading-relaxed">
                    {t("documents.truncated_warning", { chars: activeDoc.char_count.toLocaleString() })}
                  </span>
                </div>
              )}
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto">
              <div className="mx-auto max-w-3xl px-6 py-8">
                {messages.length === 0 && (
                  <div className="flex h-full min-h-[30vh] flex-col items-center justify-center text-center">
                    <Sparkles className="mb-4 h-10 w-10 text-[#9ACD32]" />
                    <p className="text-sm text-slate-400">{t("documents.ask_placeholder")}</p>
                  </div>
                )}
                {messages.map((m) => (
                  <div
                    key={m.message_id}
                    className={`message-in mb-5 flex gap-4 ${m.role === "user" ? "justify-end" : ""}`}
                    data-testid={`document-message-${m.role}`}
                  >
                    {m.role === "assistant" && (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#9ACD32] text-xs font-bold text-[#05073B]">N</div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-2xl px-5 py-3 text-[15px] leading-relaxed ${
                        m.role === "user"
                          ? "bg-[#1A1D7A] text-white"
                          : "bg-slate-900/60 text-slate-100"
                      }`}
                    >
                      <div className="whitespace-pre-wrap">{m.content}</div>
                    </div>
                  </div>
                ))}
                {asking && (
                  <div className="flex items-center gap-3 text-sm text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin text-[#9ACD32]" />
                    <span>{t("documents.thinking")}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-slate-800 bg-[#0A0C1F] p-4">
              <div className="mx-auto max-w-3xl">
                <div className="flex items-end gap-2 rounded-2xl border border-slate-700 bg-slate-900 p-3 focus-within:border-[#9ACD32]">
                  <Textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAsk();
                      }
                    }}
                    placeholder={t("documents.ask_placeholder")}
                    rows={1}
                    className="max-h-40 min-h-[40px] resize-none border-0 bg-transparent text-[15px] focus-visible:ring-0 focus-visible:ring-offset-0"
                    disabled={asking}
                    data-testid="document-question-input"
                  />
                  <Button
                    onClick={handleAsk}
                    disabled={!question.trim() || asking}
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-full bg-[#9ACD32] text-[#05073B] hover:bg-[#A8DF38]"
                    data-testid="document-ask-btn"
                  >
                    {asking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <Card className="border-slate-800 bg-[#0A0C1F] p-10 text-center">
              <FileText className="mx-auto mb-4 h-16 w-16 text-[#9ACD32]/40" />
              <p className="text-sm text-slate-400">{t("documents.upload_hint")}</p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
